// Port of TrailBivvyKit/Sources/GPX/GPXConverter.swift.

import { userImportProvenance } from '../core/domain';
import { defaultElevationThreshold, elevationStatisticsOfPoints, HikingTime } from '../core/elevation';
import { distance, isValidCoordinate, polylineLength } from '../core/geo';
import type {
  CountryCode,
  DataProvenance,
  EpochMs,
  GeoCoordinate,
  GeoPolyline,
  TrackPoint,
  Trail,
  TrailStage,
  Waypoint,
} from '../core/models';
import { GPXError } from './errors';
import { isWhitespace, omitUndefined, trimCharacters, trimWhitespaceAndNewlines } from './internal';
import {
  gpxDocumentAllTrackPoints,
  gpxWaypointRoutePoint,
  type GPXDocument,
  type GPXTrack,
  type GPXWaypoint,
} from './models';

/** Result of `GPXConverter.importedTrail`. */
export interface GPXImportedTrail {
  trail: Trail;
  stages: TrailStage[];
  waypoints: Waypoint[];
}

interface Line {
  name?: string;
  points: GPXWaypoint[];
}

/**
 * GPX → domain conversion for arbitrary imported routes (TrailBivvy is useful beyond the Via Alpina).
 *
 * Imported data is never presented as more than it is: stages are marked `userImported`, carry the user-import
 * provenance, get the neutral difficulty `moderate`, no SAC grade, no regions, no classification or hazards, and the
 * unknown-region country code `"ZZ"` because the file says nothing reliable about jurisdiction (so no country-level
 * regulation can be wrongly attached to it).
 */
export const GPXConverter = {
  /** ISO 3166 user-assigned code conventionally meaning "unknown region" (CLDR `ZZ`). */
  unknownCountry: 'ZZ' as CountryCode,

  /** Radius (m) within which a named GPX waypoint names a stage start or finish. */
  placeNameRadius: 250,

  /**
   * Elevation hysteresis (m) for recorded (timestamped) tracks, whose GPS/barometric altitude is noisier than
   * planned, DEM-derived profiles (which use `defaultElevationThreshold`).
   */
  recordedElevationThreshold: 5,

  /**
   * Builds one imported `Trail` (kind `imported`) with one `TrailStage` per track, or per route when the document
   * has no usable track. A track's segments are merged into a single stage polyline; tracks or routes with fewer
   * than two points are skipped. GPX `<wpt>`s become `Waypoint`s of kind `imported`.
   *
   * Identifiers are `import:<id>` for the trail, `import:<id>:<n>` for stages (n from 1) and `import:<id>:wpt:<n>`
   * for waypoints, where `<id>` is `idPrefix`: by default a random 8-character UUID prefix (Swift's public
   * overload); pass it for deterministic identifiers (Swift's internal overload, used by tests).
   * Names come from the GPX (`<trk>`/`<rte>` name, then metadata name), falling back to `fileName` without its
   * extension.
   *
   * Throws `GPXError` `noGeometry` when no track or route has at least two points (e.g. a waypoints-only file).
   */
  importedTrail(
    document: GPXDocument,
    fileName: string,
    importedAt: EpochMs,
    idPrefix: string = randomIDPrefix(),
  ): GPXImportedTrail {
    const trackLines: Line[] = [];
    for (const track of document.tracks) {
      const merged = mergedPoints(track);
      if (merged.length >= 2) trackLines.push(omitUndefined({ name: track.name, points: merged }));
    }
    const routeLines: Line[] = [];
    for (const route of document.routes) {
      const valid = route.points.filter((point) => isValidCoordinate(point.coordinate));
      if (valid.length >= 2) routeLines.push(omitUndefined({ name: route.name, points: valid }));
    }
    const lines = trackLines.length === 0 ? routeLines : trackLines;
    if (lines.length === 0) throw new GPXError({ kind: 'noGeometry' });

    const baseName = GPXConverter.displayBaseName(fileName);
    const trailName = document.metadata.name ?? (lines.length === 1 ? lines[0].name : undefined) ?? baseName;
    const trailID = `import:${idPrefix}`;
    const provenance = userImportProvenance(fileName, importedAt);
    const namedWaypoints = document.waypoints.filter((waypoint) => waypoint.name !== undefined);

    const stages = lines.map((line, index) => {
      const number = index + 1;
      const stageName =
        line.name ?? (lines.length === 1 ? trailName : `${document.metadata.name ?? baseName} ${number}`);
      return makeStage(`${trailID}:${number}`, trailID, number, stageName, line.points, namedWaypoints, provenance);
    });

    const trail: Trail = {
      id: trailID,
      name: trailName,
      kind: 'imported',
      countries: [],
      regions: [],
      stageIDs: stages.map((stage) => stage.id),
      historicalNames: [],
      provenance,
    };
    const waypoints = document.waypoints.map((wpt, offset) =>
      omitUndefined<Waypoint>({
        id: `${trailID}:wpt:${offset + 1}`,
        name: wpt.name ?? String(offset + 1).padStart(3, '0'),
        coordinate: copyCoordinate(wpt.coordinate),
        elevation: wpt.elevation,
        kind: { kind: 'imported' },
        note: wpt.description ?? wpt.comment,
      }),
    );
    return { trail, stages, waypoints };
  },

  /**
   * Track points that carry a timestamp, in document order, as domain `TrackPoint`s (e.g. to import a recorded
   * hike). Points without `<time>` are skipped: a `TrackPoint` never gets an invented time.
   */
  trackPoints(document: GPXDocument): TrackPoint[] {
    const out: TrackPoint[] = [];
    for (const point of gpxDocumentAllTrackPoints(document)) {
      if (point.time === undefined) continue;
      out.push(
        omitUndefined<TrackPoint>({
          coordinate: copyCoordinate(point.coordinate),
          elevation: point.elevation,
          timestamp: point.time,
        }),
      );
    }
    return out;
  },

  /** "Kandersteg – Griesalp" / "Kandersteg - Griesalp" → { from: "Kandersteg", to: "Griesalp" }. */
  splitStageName(name: string): { from: string; to: string } | undefined {
    for (const separator of [' – ', ' — ', ' - ']) {
      const parts = name.split(separator);
      if (parts.length === 2) {
        const from = trimCharacters(parts[0], isWhitespace);
        const to = trimCharacters(parts[1], isWhitespace);
        if (from !== '' && to !== '') return { from, to };
      }
    }
    return undefined;
  },

  /** File name without a trailing ".gpx" (any case); the full name if stripping would leave nothing. */
  displayBaseName(fileName: string): string {
    const trimmed = trimWhitespaceAndNewlines(fileName);
    // Swift `split(separator:)` drops empty components.
    const components = trimmed.split('/').filter((component) => component !== '');
    const lastComponent = components.length > 0 ? components[components.length - 1] : trimmed;
    if (lastComponent.toLowerCase().endsWith('.gpx')) {
      const base = lastComponent.slice(0, -4);
      if (base !== '') return base;
    }
    return lastComponent === '' ? fileName : lastComponent;
  },
};

// MARK: Helpers

/** First 8 characters of a random UUID, lower case (Swift `UUID().uuidString.lowercased().prefix(8)`). */
function randomIDPrefix(): string {
  return globalThis.crypto.randomUUID().toLowerCase().slice(0, 8);
}

function copyCoordinate(coordinate: GeoCoordinate): GeoCoordinate {
  return { latitude: coordinate.latitude, longitude: coordinate.longitude };
}

/**
 * All segments of a track joined in order; a duplicated joint vertex (within 1 m) is dropped. Invalid coordinates
 * (only possible in documents built in code, never from `GPXParser`) are skipped.
 */
function mergedPoints(track: GPXTrack): GPXWaypoint[] {
  const out: GPXWaypoint[] = [];
  for (const segment of track.segments) {
    const points = segment.points.filter((point) => isValidCoordinate(point.coordinate));
    const first = points[0];
    if (first === undefined) continue;
    const last = out[out.length - 1];
    if (last !== undefined && distance(last.coordinate, first.coordinate) < 1) out.push(...points.slice(1));
    else out.push(...points);
  }
  return out;
}

function makeStage(
  id: string,
  trailID: string,
  number: number,
  name: string,
  points: GPXWaypoint[],
  namedWaypoints: GPXWaypoint[],
  provenance: DataProvenance,
): TrailStage {
  const polyline: GeoPolyline = { points: points.map(gpxWaypointRoutePoint) };
  const isRecorded = points.some((point) => point.time !== undefined);
  const threshold = isRecorded ? GPXConverter.recordedElevationThreshold : defaultElevationThreshold;
  const stats = elevationStatisticsOfPoints(polyline.points, threshold);
  const length = polylineLength(polyline);
  // `points` has at least two elements (checked by the caller).
  const first = points[0];
  const last = points[points.length - 1];
  const endpointNames = GPXConverter.splitStageName(name);

  return {
    id,
    trailID,
    number,
    code: String(number),
    name,
    start: omitUndefined({
      name: first.name ?? nearestName(first.coordinate, namedWaypoints) ?? endpointNames?.from ?? name,
      coordinate: copyCoordinate(first.coordinate),
      elevation: first.elevation,
    }),
    finish: omitUndefined({
      name: last.name ?? nearestName(last.coordinate, namedWaypoints) ?? endpointNames?.to ?? name,
      coordinate: copyCoordinate(last.coordinate),
      elevation: last.elevation,
    }),
    countryCode: GPXConverter.unknownCountry,
    polyline,
    distance: length,
    ascent: stats.ascent,
    descent: stats.descent,
    // TrailStage requires values; 0 stands for "unknown" when the file has no elevation
    // (`polylineHasElevation` is false in that case).
    minElevation: stats.minElevation ?? 0,
    maxElevation: stats.maxElevation ?? 0,
    expectedDuration: HikingTime.estimate(length, stats.ascent, stats.descent),
    difficulty: 'moderate',
    classifications: [],
    surfaces: [],
    segments: [],
    keyPoints: [],
    waterPOIIDs: [],
    settlementPOIIDs: [],
    accommodationPOIIDs: [],
    bailoutPOIIDs: [],
    notes: [],
    dataQuality: 'userImported',
    provenance,
  };
}

function nearestName(coordinate: GeoCoordinate, waypoints: GPXWaypoint[]): string | undefined {
  let best: { name: string; distance: number } | undefined;
  for (const waypoint of waypoints) {
    const name = waypoint.name;
    if (name === undefined) continue;
    const d = distance(coordinate, waypoint.coordinate);
    if (d <= GPXConverter.placeNameRadius && d < (best?.distance ?? Infinity)) best = { name, distance: d };
  }
  return best?.name;
}
